/**
 * User Selector with search functionality
 *
 * Features:
 * - Search by name, email, or any part
 * - Single or multi-select modes
 * - Shows user avatar and email
 */

import { useState, useCallback, useMemo } from 'react';
import { Select, Spin, Avatar, Space, Typography } from 'antd';
import { UserOutlined, TeamOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { usersApi, TenantUser } from '@api/index';

const { Text } = Typography;

interface UserSelectorProps {
  value?: string | string[];
  onChange?: (value: string | string[] | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  multiple?: boolean;
  excludeUserIds?: string[];
}

export default function UserSelector({
  value,
  onChange,
  placeholder = 'Search users...',
  disabled = false,
  multiple = false,
  excludeUserIds = [],
}: UserSelectorProps) {
  const [searchValue, setSearchValue] = useState('');

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users', 'tenant-members'],
    queryFn: () => usersApi.listTenantMembers(),
  });

  const filteredUsers = useMemo(() => {
    let result = users.filter((tenantUser) => !excludeUserIds.includes(tenantUser.userId));

    if (searchValue) {
      const lower = searchValue.toLowerCase();
      result = result.filter(
        (tenantUser) =>
          tenantUser.user.fullName.toLowerCase().includes(lower) ||
          tenantUser.user.email.toLowerCase().includes(lower)
      );
    }

    return result;
  }, [users, searchValue, excludeUserIds]);

  const handleSearch = useCallback((val: string) => {
    setSearchValue(val);
  }, []);

  const handleChange = useCallback(
    (selectedValue: string | string[]) => {
      onChange?.(selectedValue);
      setSearchValue('');
    },
    [onChange]
  );

  const handleClear = useCallback(() => {
    onChange?.(multiple ? [] : undefined);
    setSearchValue('');
  }, [onChange, multiple]);

  const options = useMemo(() => {
    return filteredUsers.map((tenantUser) => ({
      value: tenantUser.userId,
      label: tenantUser.user.fullName,
      tenantUser,
    }));
  }, [filteredUsers]);

  const getInitials = (name: string) => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getAvatarColor = (id: string) => {
    const colors = [
      '#f56a00',
      '#7265e6',
      '#ffbf00',
      '#00a2ae',
      '#1890ff',
      '#eb2f96',
      '#52c41a',
      '#722ed1',
    ];
    const index = id.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const renderOption = (option: any) => {
    const tenantUser = option.data.tenantUser as TenantUser;
    return (
      <Space>
        <Avatar
          size="small"
          style={{ backgroundColor: getAvatarColor(tenantUser.userId) }}
        >
          {getInitials(tenantUser.user.fullName)}
        </Avatar>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Text>{tenantUser.user.fullName}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {tenantUser.user.email}
          </Text>
        </div>
      </Space>
    );
  };

  const tagRender = useCallback(
    (props: { value: string; label: React.ReactNode; closable: boolean; onClose: () => void }) => {
      const tenantUser = users.find((u) => u.userId === props.value);
      if (!tenantUser) return <span>{props.label}</span>;

      return (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '2px 8px',
            background: '#f5f5f5',
            borderRadius: 4,
            marginRight: 4,
          }}
        >
          <Avatar
            size={16}
            style={{ backgroundColor: getAvatarColor(tenantUser.userId), fontSize: 10 }}
          >
            {getInitials(tenantUser.user.fullName)}
          </Avatar>
          <span>{tenantUser.user.fullName}</span>
          {props.closable && (
            <span
              onClick={props.onClose}
              style={{ cursor: 'pointer', marginLeft: 4 }}
            >
              ×
            </span>
          )}
        </span>
      );
    },
    [users]
  );

  const selectedTenantUser = !multiple && value ? users.find((u) => u.userId === value) : null;

  return (
    <Select
      mode={multiple ? 'multiple' : undefined}
      showSearch
      allowClear
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      loading={isLoading}
      filterOption={false}
      searchValue={searchValue}
      onSearch={handleSearch}
      onChange={handleChange}
      onClear={handleClear}
      options={options}
      tagRender={multiple ? tagRender : undefined}
      notFoundContent={
        isLoading ? <Spin size="small" /> : <Text type="secondary">No users found</Text>
      }
      style={{ width: '100%' }}
      suffixIcon={multiple ? <TeamOutlined /> : <UserOutlined />}
      optionRender={renderOption}
      labelRender={
        !multiple && selectedTenantUser
          ? () => (
              <Space>
                <Avatar
                  size={20}
                  style={{ backgroundColor: getAvatarColor(selectedTenantUser.userId) }}
                >
                  {getInitials(selectedTenantUser.user.fullName)}
                </Avatar>
                {selectedTenantUser.user.fullName}
              </Space>
            )
          : undefined
      }
    />
  );
}
